import { useRef, useEffect, useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import './InteractiveMap.css'
import type { Store } from '@/types'

const getMapTilerStyleUrl = (): string => {
    if (typeof window !== 'undefined' && window.AppConfig?.api?.mapTilerStyleUrl) {
        return window.AppConfig.api.mapTilerStyleUrl
    }
    return 'https://api.maptiler.com/maps/c009d368-07c5-496e-8047-e69432e7af2b/style.json?key=Kaj7hj4spq6OTB0wtwjG'
}

interface InteractiveMapProps {
    stores: Store[]
    selectedId: string | null
    onSelectStore: (id: string) => void
}

export function InteractiveMap({ stores, selectedId, onSelectStore }: InteractiveMapProps) {
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<maplibregl.Map | null>(null)
    const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map())

    const getBounds = useCallback(() => {
        if (stores.length === 0) return null

        const lats = stores.map(s => s.addressLocation?.lat ?? 0)
        const lngs = stores.map(s => s.addressLocation?.long ?? 0)

        return new maplibregl.LngLatBounds(
            [Math.min(...lngs ?? []) - 0.02, Math.min(...lats ?? []) - 0.02],
            [Math.max(...lngs ?? []) + 0.02, Math.max(...lats ?? []) + 0.02]
        )
    }, [stores])

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return

        const map = new maplibregl.Map({
            container: mapContainerRef.current,
            style: getMapTilerStyleUrl(),
            center: [144.9631, -37.8136],
            zoom: 12,
            attributionControl: false,
        })

        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')

        map.on('load', () => {
            const bounds = getBounds()
            if (bounds) {
                map.fitBounds(bounds, {
                    padding: { top: 80, bottom: 200, left: 40, right: 40 },
                    maxZoom: 14,
                })
            }
        })

        mapRef.current = map

        return () => {
            map.remove()
            mapRef.current = null
        }
    }, [getBounds])

    useEffect(() => {
        const map = mapRef.current
        if (!map) return

        markersRef.current.forEach(marker => marker.remove())
        markersRef.current.clear()

        stores.forEach((store, index) => {
            const storeId = store.storeId
            const isSelected = storeId === selectedId

            const el = document.createElement('div')
            el.className = 'store-marker'

            el.setAttribute('tabindex', '0')
            el.setAttribute('role', 'button')
            el.setAttribute('aria-label', `${store.name}, ${store.state}${isSelected ? ', selected' : ''}`)
            el.setAttribute('data-store-index', String(index))

            el.innerHTML = `
        <div class="marker-container ${isSelected ? 'selected' : ''}">
          <svg viewBox="0 0 24 30" class="marker-pin">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 18 12 18s12-9 12-18c0-6.627-5.373-12-12-12z" />
            <circle cx="12" cy="12" r="5" fill="white" />
          </svg>
          ${isSelected ? `
            <div class="marker-pulse"></div>
            <div class="marker-label">${store.name}</div>
          ` : ''}
        </div>
      `

            el.addEventListener('click', () => {
                onSelectStore(storeId)
            })

            el.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelectStore(storeId)
                }
            })

            const marker = new maplibregl.Marker({
                element: el,
                anchor: 'bottom',
            })
                .setLngLat([store.addressLocation?.long ?? 0, store.addressLocation?.lat ?? 0])
                .addTo(map)

            markersRef.current.set(storeId, marker)
        })
    }, [stores, selectedId, onSelectStore])

    useEffect(() => {
        const map = mapRef.current
        if (!map || !selectedId) return

        const store = stores.find(s => (s.storeId) === selectedId)
        if (store) {
            map.easeTo({
                center: [store.addressLocation?.long ?? 0, store.addressLocation?.lat ?? 0],
                duration: 500,
                offset: [0, -50],
            })
        }
    }, [selectedId, stores])

    return (
        <>
            <div ref={mapContainerRef} className="w-full h-full" />

            <div
                className="lg:hidden absolute inset-x-0 bottom-0 h-36 pointer-events-none"
                style={{
                    background: 'linear-gradient(to top, var(--color-surface-primary) 0%, transparent 100%)',
                }}
            />
        </>
    )
}
